import React from 'react';
import { create } from 'zustand';
import { StargraveBuff, StargraveEquipment, StargraveSoldierProfile } from '../types';
import { stargraveEquipment, stargraveSoldiers } from '../data/stargrave/coreRules';
import { stargraveBuffs } from '../data/stargrave/buffs';
import { stargraveCustomContentService } from '../services/stargraveCustomContentService';

interface StargraveCustomStore {
  customEquipment: StargraveEquipment[];
  customSoldiers: StargraveSoldierProfile[];
  customBuffs: StargraveBuff[];
  addCustomEquipment: (data: Omit<StargraveEquipment, 'id' | 'isCustom'>) => void;
  updateCustomEquipment: (id: string, data: Omit<StargraveEquipment, 'id' | 'isCustom'>) => void;
  deleteCustomEquipment: (id: string) => void;
  addCustomSoldier: (data: Omit<StargraveSoldierProfile, 'id' | 'isCustom'>) => void;
  updateCustomSoldier: (id: string, data: Omit<StargraveSoldierProfile, 'id' | 'isCustom'>) => void;
  deleteCustomSoldier: (id: string) => void;
  addCustomBuff: (data: Omit<StargraveBuff, 'id' | 'isCustom'>) => void;
  updateCustomBuff: (id: string, data: Omit<StargraveBuff, 'id' | 'isCustom'>) => void;
  deleteCustomBuff: (id: string) => void;
}

export const useStargraveCustomStore = create<StargraveCustomStore>((set) => ({
  customEquipment: stargraveCustomContentService.getAllEquipment(),
  customSoldiers: stargraveCustomContentService.getAllSoldiers(),
  customBuffs: stargraveCustomContentService.getAllBuffs(),

  addCustomEquipment: (data) => {
    const created = stargraveCustomContentService.createEquipment(data);
    set((s) => ({ customEquipment: [...s.customEquipment, created] }));
  },

  updateCustomEquipment: (id, data) => {
    const updated = stargraveCustomContentService.updateEquipment(id, data);
    if (!updated) return;
    set((s) => ({ customEquipment: s.customEquipment.map((item) => (item.id === id ? updated : item)) }));
  },

  deleteCustomEquipment: (id) => {
    stargraveCustomContentService.deleteEquipment(id);
    set((s) => ({ customEquipment: s.customEquipment.filter((item) => item.id !== id) }));
  },

  addCustomSoldier: (data) => {
    const created = stargraveCustomContentService.createSoldier(data);
    set((s) => ({ customSoldiers: [...s.customSoldiers, created] }));
  },

  updateCustomSoldier: (id, data) => {
    const updated = stargraveCustomContentService.updateSoldier(id, data);
    if (!updated) return;
    set((s) => ({ customSoldiers: s.customSoldiers.map((profile) => (profile.id === id ? updated : profile)) }));
  },

  deleteCustomSoldier: (id) => {
    stargraveCustomContentService.deleteSoldier(id);
    set((s) => ({ customSoldiers: s.customSoldiers.filter((profile) => profile.id !== id) }));
  },

  addCustomBuff: (data) => {
    const created = stargraveCustomContentService.createBuff(data);
    set((s) => ({ customBuffs: [...s.customBuffs, created] }));
  },

  updateCustomBuff: (id, data) => {
    const updated = stargraveCustomContentService.updateBuff(id, data);
    if (!updated) return;
    set((s) => ({ customBuffs: s.customBuffs.map((buff) => (buff.id === id ? updated : buff)) }));
  },

  deleteCustomBuff: (id) => {
    stargraveCustomContentService.deleteBuff(id);
    set((s) => ({ customBuffs: s.customBuffs.filter((buff) => buff.id !== id) }));
  },
}));

/** Static core catalog plus user customs — for non-React code (rules/services). */
export function getAllStargraveEquipment(): StargraveEquipment[] {
  return [...stargraveEquipment, ...useStargraveCustomStore.getState().customEquipment];
}

export function getAllStargraveSoldiers(): StargraveSoldierProfile[] {
  return [...stargraveSoldiers, ...useStargraveCustomStore.getState().customSoldiers];
}

export function getAllStargraveBuffs(): StargraveBuff[] {
  return [...stargraveBuffs, ...useStargraveCustomStore.getState().customBuffs];
}

/** Reactive merged catalogs — for components. */
export function useStargraveEquipment(): StargraveEquipment[] {
  const custom = useStargraveCustomStore((s) => s.customEquipment);
  return React.useMemo(() => [...stargraveEquipment, ...custom], [custom]);
}

export function useStargraveSoldiers(): StargraveSoldierProfile[] {
  const custom = useStargraveCustomStore((s) => s.customSoldiers);
  return React.useMemo(() => [...stargraveSoldiers, ...custom], [custom]);
}

export function useStargraveBuffs(): StargraveBuff[] {
  const custom = useStargraveCustomStore((s) => s.customBuffs);
  return React.useMemo(() => [...stargraveBuffs, ...custom], [custom]);
}
